import { Can } from 'shared/permission';
import _ from 'lodash';
import { types, resource } from 'constants/accessControl';
import { title } from 'constants/tabs/title';
import { RouteHideContext, RouteConfig } from 'router/types';
import { lazyWithRetry } from 'utils/helpers';
import { UserClaimTypes } from 'constants/enumTypes1';

export const allRouters = [
  {
    menuItem: true,
    id: 2,
    name: 'dashboard',
    brandSettings: { editMode: true },
    showConversion: true,
    timeZoneSettings: { editMode: true },
    component: lazyWithRetry(() => import('features/dashboard/pages/Dashboard')),
    icon: 'DashboardIcon',
    path: '/dashboard',
    key: '/dashboard',
    exact: true,
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'dashboard',
        path: '/dashboard',
      },
    ],
    hide: () => !!Can({
      type: types.manage,
      rule: [
        `${resource.dashboards}.${resource.customersData}`,
        `${resource.dashboards}.${resource.bettingData}`,
        `${resource.dashboards}.${resource.paymentsData}`,
        `${resource.dashboards}.${resource.bonusesData}`,
        `${resource.dashboards}.${resource.financialTrends}`,
        `${resource.dashboards}.${resource.customersData}`,
      ],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 16,
    name: 'brands',
    component: lazyWithRetry(() => import('features/brands/pages/Brands')),
    exact: true,
    icon: 'PartnerIcon',
    path: '/brands',
    key: '/brands',
    partnerSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'brands',
        path: '/brands',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.brands}.${resource.brands}`,
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 3,
    name: 'players',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    icon: 'PlayersIcon',
    path: '/allPlayers',
    key: '/allPlayers',
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.players}.${resource.players}`, `${resource.players}.${resource.testPlayers}`, `${resource.players}.${resource.kyc}`],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 4,
    name: 'payments',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    icon: 'FinancesIcon',
    path: '/payments',
    key: '/payments',
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.financials}.${resource.withdrawals}`, `${resource.financials}.${resource.deposits}`],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 5,
    name: 'bets',
    component: lazyWithRetry(() => import('features/bets/pages/Bets')),
    exact: true,
    icon: 'MenuBetsIcon',
    path: '/bets',
    key: '/bets',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bets',
        path: '/bets',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.bets}.${resource.casinoBets}`, `${resource.bets}.${resource.sportBets}`],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 6,
    name: 'bonusManagement',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    icon: 'BonusEngineIcon',
    path: '/bonusManagement',
    key: '/bonusManagement',
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.bonusManagement}.${resource.bonus}`, `${resource.bonusManagement}.${resource.cashback}`, `${resource.bonusManagement}.${resource.referralBonus}`],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 7,
    name: 'riskManagement',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    icon: 'RiskIcon',
    path: '/riskManagement',
    key: '/riskManagement',
    hide: () => !!Can({
      type: types.view,
      rule: [
        `${resource.riskManagement}.${resource.balanceAdjustment}`,
        `${resource.riskManagement}.${resource.bulkStatusUpdate}`,
        `${resource.riskManagement}.${resource.similarAccounts}`,
        `${resource.riskManagement}.${resource.blacklist}`,
        `${resource.riskManagement}.${resource.previousAccounts}`,
        `${resource.riskManagement}.${resource.bulkSelfExclusion}`,
      ],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 101,
    name: 'tools',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    icon: 'ToolsIcon',
    path: '/tool',
    key: '/tool',
    hide: () => !!Can({
      type: types.view,
      rule: [
        `${resource.tools}.${resource.verticalTags}`,
        `${resource.tools}.${resource.horizontalTags}`,
        `${resource.tools}.${resource.brandAutomation}`,
        `${resource.tools}.${resource.gameSegments}`,
        `${resource.tools}.${resource.playerSegments}`,
        `${resource.tools}.${resource.reasonsSystem}`,
        `${resource.tools}.${resource.agSentData}`,
      ],
      children: false,
      fallback: true,
    })
      && !!Can({
        type: types.export,
        rule: `${resource.players}.${resource.players}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.bets}.${resource.casinoBets}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.bets}.${resource.sportBets}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.financials}.${resource.deposits}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.financials}.${resource.withdrawals}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.playersReports}.${resource.playersTotals}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.playersReports}.${resource.inactiveAccounts}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.bonusesReport}.${resource.bonusesReport}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.auditLogs}.${resource.playerLogs}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.auditLogs}.${resource.selfieKYCLogs}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.auditLogs}.${resource.gamStopLogs}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.auditLogs}.${resource.nraLogs}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.auditLogs}.${resource.userLogs}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.auditLogs}.${resource.userManagementLogs}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.communications}.${resource.pushNotifications}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.businessIntelligence}.${resource.betAggregations}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.compliance}.${resource.largeTransaction}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.financialReports}.${resource.playersWallets}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.deviceSessions}.${resource.deviceSessions}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 182,
    menuItem: true,
    name: 'gamification',
    icon: 'PromotionIcon',
    component: lazyWithRetry(() => import('features/gamification/pages/GamificationList')),
    exact: true,
    path: '/gamification',
    key: '/gamification',
    timeZoneSettings: { editMode: true },
    brandSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gamification:gamification',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.gamification}.${resource.gamification}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 9,
    menuItem: true,
    name: 'crm',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    icon: 'CommsIcon',
    path: '/communications',
    key: '/communications',
    hide: () => !!Can({
      type: types.view,
      rule: [
        `${resource.communications}.${resource.inboxMessages}`,
        `${resource.communications}.${resource.massMessages}`,
        `${resource.communications}.${resource.notificationTemplates}`,
        `${resource.communications}.${resource.systemNotifications}`,
        `${resource.communications}.${resource.pushNotifications}`,
        `${resource.communications}.${resource.playerComplaints}`,
      ],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 106,
    menuItem: true,
    menuParentId: 9,
    brandSettings: { editMode: true },
    name: 'massMessages',
    component: lazyWithRetry(() => import('pages/MassMessages')),
    exact: true,
    path: '/massMessages',
    key: '/massMessages',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'massMessages',
        path: '/massMessages',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.massMessages}`,
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 11,
    menuParentId: 9,
    name: 'inboxMessages',
    component: lazyWithRetry(() => import('features/crm/pages/InboxMessages')),
    exact: true,
    path: '/inboxMessages',
    key: '/inboxMessages',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'inboxMessages',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.inboxMessages}`,
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 13,
    name: 'cms',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    icon: 'BuilderIcon',
    path: '/CMS',
    key: '/CMS',
    hide: () => !!Can({
      type: types.view,
      rule: [
        `${resource.contentManagement}.${resource.fileManager}`,
        `${resource.contentManagement}.${resource.translations}`,
        `${resource.contentManagement}.${resource.cashierSettings}`,
        `${resource.contentManagement}.${resource.groups}`,
        `${resource.contentManagement}.${resource.games}`,
        `${resource.contentManagement}.${resource.lobbyManagement}`,
        `${resource.contentManagement}.${resource.gameTypes}`,
      ],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 15,
    name: 'reporting',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    icon: 'ReportingIcon',
    path: '/reporting',
    key: '/reporting',
    hide: () => !!Can({
      type: types.view,
      rule: [
        `${resource.playersReports}.${resource.closedAccounts}`,
        `${resource.playersReports}.${resource.playersTotals}`,
        `${resource.financialReports}.${resource.balanceAdjustments}`,
        `${resource.compliance}.${resource.largeTransaction}`,
        `${resource.compliance}.${resource.negativeBalance}`,
        `${resource.businessIntelligence}.${resource.betAggregations}`,
        `${resource.businessIntelligence}.${resource.paymentAggregations}`,
        `${resource.providers}.${resource.providersReport}`,
        `${resource.bonusesReport}.${resource.bonusesReport}`,
        `${resource.playersReports}.${resource.inactiveAccounts}`,
        `${resource.playersReports}.${resource.topPlayers}`,
        `${resource.deviceSessions}.${resource.deviceSessions}`,
        `${resource.taxation}.${resource.betting}`,
      ],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 198,
    name: 'regulatory',
    component: lazyWithRetry(() => import('features/regulatory/pages/RegulatoryReports')),
    exact: true,
    path: '/regulatory',
    key: '/regulatory',
    icon: 'RegulatoryIcon',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'regulatory',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [
        `${resource.romanianRegulatory}.${resource.romanianRegulatory}`,
        `${resource.greekRegulatory}.${resource.greekRegulatoryReport}`,
        `${resource.estoniaRegulatory}.${resource.estoniaRegulatory}`,
        `${resource.cypriotRegulatory}.${resource.cyprusRegulatoryReport}`,
      ],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 17,
    name: 'partners',
    component: lazyWithRetry(() => import('features/partners/pages/Partners')),
    exact: true,
    icon: 'BrandIcon',
    path: '/partners',
    key: '/partners',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'partners',
        path: '/partners',
      },
    ],
    hide: (params: RouteHideContext) => _.isEmpty(params?.partnerAccessIds)
      || !!Can({
        type: types.view,
        rule: `${resource.partners}.${resource.partners}`,
        children: false,
        fallback: true,
      }),
  },
  {
    menuItem: true,
    id: 18,
    name: 'userManagement',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    icon: 'UserManagementIcon',
    path: '/userManagement',
    key: '/userManagement',
    hide: (params: RouteHideContext) => _.isEmpty(params?.partnerAccessIds)
      || !!Can({
        type: types.view,
        rule: [
          `${resource.userManagement}.${resource.permissions}`,
          `${resource.userManagement}.${resource.permissionGroups}`,
          `${resource.userManagement}.${resource.users}`,
          `${resource.userManagement}.${resource.ipWhitelist}`,
          `${resource.userManagement}.${resource.requests}`,
        ],
        children: false,
        fallback: true,
      }),
  },
  {
    menuItem: true,
    id: 107,
    name: 'auditLog',
    component: lazyWithRetry(() => import('features/auditLogs/pages/AuditLogs')),
    exact: true,
    icon: 'AuditLogIcon',
    path: '/auditLogs',
    key: '/auditLogs',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'auditLogs',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [
        `${resource.auditLogs}.${resource.userLogs}`,
        `${resource.auditLogs}.${resource.playerLogs}`,
        `${resource.auditLogs}.${resource.userManagementLogs}`,
        `${resource.auditLogs}.${resource.episLogs}`,
        `${resource.auditLogs}.${resource.gamStopLogs}`,
        `${resource.auditLogs}.${resource.nraLogs}`,
        `${resource.auditLogs}.${resource.selfieKYCLogs}`,
      ],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 176,
    menuItem: true,
    name: 'developmentTools',
    component: lazyWithRetry(() => import('features/devTools/pages')),
    exact: true,
    icon: 'DevToolsIcon',
    path: '/devTools',
    key: '/devTools',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'developmentTools',
      },
    ],
    hide: () => !!Can({
      claims: [UserClaimTypes.Developer],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 19,
    name: 'system',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    icon: 'ProductsIcon',
    path: '/system',
    key: '/system',
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.systemSettings}.${resource.systemConversion}`],
      children: false,
      fallback: true,
    })
      && !!Can({
        type: types.manage,
        rule: `${resource.systemSettings}.${resource.releaseNotes}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.view,
        rule: `${resource.systemSettings}.${resource.dataFormatting}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.view,
        rule: `${resource.systemSettings}.${resource.superiorList}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 23,
    component: lazyWithRetry(() => import('pages/Player')),
    exact: true,
    menuParentId: 81,
    path: '/players/:playerId/*',
    key: '/players/:playerId/*',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'players',
        path: '/players',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.players}.${resource.players}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 24,
    component: lazyWithRetry(() => import('features/partners/pages/CreatePartner')),
    exact: true,
    menuParentId: 17,
    path: '/partners/create',
    key: '/partners/create',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'partners',
        path: '/partners',
      },
      {
        name: 'create',
        path: '/partners/create',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.partners}.${resource.partners}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 25,
    component: lazyWithRetry(() => import('features/partners/pages/EditPartner')),
    exact: true,
    menuParentId: 17,
    path: '/partners/:id',
    key: '/partners/:id',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'partners',
        path: '/partners',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.edit,
      rule: `${resource.partners}.${resource.partners}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 26,
    menuParentId: 18,
    menuItem: true,
    name: 'users',
    component: lazyWithRetry(() => import('features/userManagement/pages/Users')),
    exact: true,
    path: '/users',
    key: '/users',
    partnerSettings: { editMode: true, includeMainPartner: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'users',
        path: '/users',
      },
    ],
    hide: (params: RouteHideContext) => _.isEmpty(params?.partnerAccessIds)
      || !!Can({
        type: types.view,
        rule: `${resource.userManagement}.${resource.users}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 27,
    menuParentId: 18,
    menuItem: true,
    name: 'permissionGroups',
    component: lazyWithRetry(() => import('features/userManagement/pages/PermissionGroups')),
    exact: true,
    path: '/permissionGroups',
    key: '/permissionGroups',
    partnerSettings: { editMode: true, includeMainPartner: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'permissionGroups',
        path: '/permissionGroups',
      },
    ],
    hide: (params: RouteHideContext) => _.isEmpty(params?.partnerAccessIds)
      || !!Can({
        type: types.view,
        rule: `${resource.userManagement}.${resource.permissionGroups}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 28,
    menuParentId: 18,
    menuItem: true,
    name: 'permissions',
    component: lazyWithRetry(() => import('features/userManagement/pages/Permissions')),
    exact: true,
    path: '/permissions',
    key: '/permissions',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'permissions',
      },
    ],
    hide: (params: RouteHideContext) => _.isEmpty(params?.partnerAccessIds)
      || !!Can({
        type: types.view,
        rule: `${resource.userManagement}.${resource.permissions}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 29,
    name: 'Permission Group Edit',
    menuParentId: 27,
    component: lazyWithRetry(() => import('features/userManagement/pages/EditPermissionGroup')),
    exact: true,
    path: '/permissionGroups/:id/edit',
    key: '/permissionGroups/:id/edit',
    partnerSettings: { viewMode: true, includeMainPartner: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'permissionGroups',
        path: '/permissionGroups',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.edit,
      rule: `${resource.userManagement}.${resource.permissionGroups}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 30,
    menuParentId: 27,
    name: 'View Permission Group',
    component: lazyWithRetry(() => import('features/userManagement/pages/ViewPermissionGroup')),
    exact: true,
    path: '/permissionGroups/:id/view',
    key: '/permissionGroups/:id/view',
    partnerSettings: { viewMode: true, includeMainPartner: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'permissionGroups',
        path: '/permissionGroups',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.userManagement}.${resource.permissionGroups}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 32,
    name: 'Compose',
    menuParentId: 106,
    component: lazyWithRetry(() => import('pages/Compose')),
    exact: true,
    path: '/massMessages/compose',
    key: '/compose',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'massMessages',
        path: '/massMessages',
      },
      {
        name: 'composeOptions',
        path: '/massMessages/chooseCompose',
      },
      {
        name: 'compose',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.communications}.${resource.massMessages}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 171,
    name: 'Compose',
    menuParentId: 106,
    component: lazyWithRetry(() => import('pages/Compose')),
    exact: true,
    path: '/massMessages/compose/templates/:templateId',
    key: '/compose',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'massMessages',
        path: '/massMessages',
      },
      {
        name: 'composeOptions',
        path: '/massMessages/chooseCompose',
      },
      {
        name: 'compose',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.communications}.${resource.massMessages}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 33,
    menuParentId: 13,
    menuItem: true,
    name: 'translations',
    component: lazyWithRetry(() => import('pages/Translations')),
    exact: true,
    path: '/translations',
    key: '/translations',
    brandSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'translations',
        path: '/translations',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.contentManagement}.${resource.translations}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 34,
    menuParentId: 9,
    menuItem: true,
    name: 'systemNotifications',
    component: lazyWithRetry(() => import('features/crm/pages/SystemNotifications')),
    exact: true,
    path: '/systemNotifications',
    key: '/systemNotifications',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'systemNotifications',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.systemNotifications}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 35,
    name: 'CreateCustomTemplate',
    component: lazyWithRetry(() => import('pages/CreateEditNotificationTemplate')),
    exact: true,
    menuParentId: 70,
    path: '/notificationTemplates/create',
    key: '/notificationTemplates/create',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'notificationTemplates',
        path: '/notificationTemplates',
      },
      {
        name: 'create',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.communications}.${resource.notificationTemplates}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 36,
    name: 'EditCustomTemplate',
    menuParentId: 70,
    component: lazyWithRetry(() => import('pages/CreateEditNotificationTemplate')),
    exact: true,
    path: '/notificationTemplates/:templateId/edit',
    key: '/notificationTemplates/:templateId/edit',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'notificationTemplates',
        path: '/notificationTemplates',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.edit,
      rule: `${resource.communications}.${resource.notificationTemplates}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 37,
    menuParentId: 34,
    name: 'View System Notification',
    component: lazyWithRetry(() => import('features/crm/pages/ViewTemplate')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/systemNotifications/:systemTemplateId',
    key: '/systemNotifications/:systemTemplateId',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'systemNotifications',
        path: '/systemNotifications',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.systemNotifications}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 38,
    name: 'ComposeTemplates',
    menuParentId: 106,
    brandSettings: { viewMode: true },
    component: lazyWithRetry(() => import('pages/ComposeTemplates')),
    exact: true,
    path: '/massMessages/chooseCompose',
    key: '/massMessages/chooseCompose',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'massMessages',
        path: '/massMessages',
      },
      {
        name: 'composeOptions',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.communications}.${resource.massMessages}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 40,
    name: 'EditSystemTemplate',
    menuParentId: 34,
    component: lazyWithRetry(() => import('features/crm/pages/EditSystemTemplate')),
    exact: true,
    path: '/systemNotifications/:templateId/edit',
    key: '/systemNotifications/:templateId/edit',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'systemNotifications',
        path: '/systemNotifications',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.edit,
      rule: `${resource.communications}.${resource.systemNotifications}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 41,
    menuParentId: 13,
    menuItem: true,
    name: 'fileManager',
    component: lazyWithRetry(() => import('pages/FileManager')),
    exact: true,
    path: '/fileManager',
    key: '/fileManager',
    brandSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'fileManager',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.contentManagement}.${resource.fileManager}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 163,
    menuParentId: 6,
    name: 'campaigns',
    menuItem: true,
    brandSettings: { editMode: true },
    component: lazyWithRetry(() => import('pages/Campaigns')),
    exact: true,
    path: '/campaigns',
    key: '/campaigns',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.bonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 1028,
    menuParentId: 6,
    name: 'campaignsV2Units',
    menuItem: true,
    brandSettings: { isNewVersion: true, editMode: true },
    component: lazyWithRetry(() => import('features/bonusManagement/pages/Units')),
    exact: true,
    path: '/campaigns/v2/units',
    key: '/campaigns/v2/units',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaignsV2Units',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 42,
    menuParentId: 6,
    menuItem: true,
    name: 'bonuses',
    component: lazyWithRetry(() => import('pages/Bonuses')),
    exact: true,
    path: '/bonuses',
    key: '/bonuses',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bonuses',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.bonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 46,
    menuParentId: 6,
    menuItem: true,
    name: 'promoCodes',
    component: lazyWithRetry(() => import('pages/PromoCodes')),
    exact: true,
    brandSettings: { editMode: true },
    path: '/promoCodes',
    key: '/promoCodes',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'promoCodes',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.bonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 45,
    menuParentId: 6,
    menuItem: true,
    name: 'cashback',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/cashback/Cashbacks')),
    exact: true,
    brandSettings: { editMode: true },
    path: '/cashbacks',
    key: '/cashbacks',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.cashback}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 47,
    menuParentId: 6,
    menuItem: true,
    name: 'referral',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/referral/Referrals')),
    exact: true,
    brandSettings: { editMode: true },
    path: '/referrals',
    key: '/referrals',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'referral',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.referralBonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 51,
    name: 'Games in Segment',
    menuParentId: 52,
    component: lazyWithRetry(() => import('pages/SegmentGames')),
    exact: true,
    path: '/segments/:id/games',
    key: '/segments/:id/games',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gameSegments',
        path: '/segments',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.gameSegments}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 53,
    name: 'playersInSegment',
    menuParentId: 50,
    component: lazyWithRetry(() => import('pages/SegmentPlayers')),
    exact: true,
    path: '/segments/:id/players',
    key: '/segments/:id/players',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'playerSegments',
        path: '/segments',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.playerSegments}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 58,
    name: 'UsedPromoCodes',
    menuParentId: 46,
    component: lazyWithRetry(() => import('pages/UsedPromoCodes')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/promoCodes/:id/used/:type',
    key: '/promoCodes/:id/used/:type',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'promoCodes',
        path: '/promoCodes',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.bonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 59,
    name: 'ViewMassMessage',
    menuParentId: 106,
    component: lazyWithRetry(() => import('pages/ViewMessage')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/massMessages/:messageId',
    key: '/massMessages/:messageId',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'massMessages',
        path: '/massMessages',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.massMessages}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 61,
    name: 'Bonus Statistics',
    menuParentId: 42,
    component: lazyWithRetry(() => import('pages/BonusStatistics')),
    exact: true,
    path: '/bonuses/:bonusId/:bonusType/statistics',
    key: '/bonuses/:bonusId/:bonusType/statistics',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bonuses',
        path: '/bonuses',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.bonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 63,
    menuItem: true,
    menuParentId: 7,
    name: 'similarAccounts',
    component: lazyWithRetry(() => import('features/riskManagement/pages/SimilarAccountsGroups')),
    exact: true,
    path: '/similarAccounts',
    key: '/similarAccounts',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'similarAccounts',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.similarAccounts}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 160,
    menuParentId: 63,
    name: 'similarAccounts',
    component: lazyWithRetry(() => import('features/riskManagement/pages/SimilarAccounts')),
    exact: true,
    path: '/similarAccount',
    key: '/similarAccount',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'similarAccounts',
        path: '/similarAccounts',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.similarAccounts}`,
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 64,
    menuParentId: 7,
    name: 'bulkActions',
    component: lazyWithRetry(() => import('features/riskManagement/pages/BulkActions')),
    exact: true,
    path: '/bulkActions',
    key: '/bulkActions',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bulkActions',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [
        `${resource.riskManagement}.${resource.balanceAdjustment}`,
        `${resource.riskManagement}.${resource.bulkStatusUpdate}`,
        `${resource.riskManagement}.${resource.bulkBlockProvider}`,
        `${resource.riskManagement}.${resource.bulkBlockAccess}`,
        `${resource.riskManagement}.${resource.bulkSelfExclusion}`,
      ],
      children: false,
      fallback: true,
    })
      && !!Can({
        type: types.manage,
        rule: `${resource.gamification}.${resource.gamification}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 65,
    component: lazyWithRetry(() => import('features/riskManagement/pages/StatusUpdatedPlayers')),
    exact: true,
    menuParentId: 64,
    brandSettings: { viewMode: true },
    path: '/bulkActions/statusUpdates/:id/players',
    key: '/bulkActions/statusUpdates/:id/players',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bulkActions',
        path: `/bulkActions?tab=${title.bulkActions.statusUpdates}`,
      },
      {
        name: 'statusUpdate',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.bulkStatusUpdate}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 66,
    component: lazyWithRetry(() => import('pages/SelfExcludedPlayerReport')),
    exact: true,
    menuParentId: 64,
    brandSettings: { viewMode: true },
    path: '/bulkActions/selfExclusions/:id/players',
    key: '/bulkActions/selfExclusions/:id/players',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bulkActions',
        path: `/bulkActions?tab=${title.bulkActions.selfExclusions}`,
      },
      {
        name: 'selfExcludedPlayers',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.bulkSelfExclusion}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 67,
    name: 'MassMessage Edit',
    menuParentId: 106,
    component: lazyWithRetry(() => import('pages/Compose')),
    exact: true,
    path: '/massMessages/:messageId/edit',
    key: '/massMessages/:messageId/edit',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'massMessages',
        path: '/massMessages',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.edit,
      rule: `${resource.communications}.${resource.massMessages}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 68,
    component: lazyWithRetry(() => import('pages/BalanceAdjustmentTransactionReport')),
    menuParentId: 64,
    exact: true,
    path: '/bulkActions/balanceAdjustments/:id/transactions',
    key: '/bulkActions/balanceAdjustments/:id/transactions',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bulkActions',
        path: `/bulkActions?tab=${title.bulkActions.balanceAdjustments}`,
      },
      {
        name: 'balanceAdjustments',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.balanceAdjustment}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 69,
    name: 'MassMessage Recipients',
    menuParentId: 106,
    component: lazyWithRetry(() => import('pages/MassMessageRecipients')),
    exact: true,
    path: '/massMessages/:messageId/recipients',
    key: '/massMessages/:messageId/recipients',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'massMessages',
        path: '/massMessages',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.massMessages}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 70,
    menuParentId: 9,
    menuItem: true,
    name: 'notificationTemplates',
    component: lazyWithRetry(() => import('pages/NotificationTemplates')),
    exact: true,
    path: '/notificationTemplates',
    key: '/notificationTemplates',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'notificationTemplates',
        path: '/notificationTemplates',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.notificationTemplates}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 71,
    name: 'View Custom Template',
    menuParentId: 70,
    component: lazyWithRetry(() => import('features/crm/pages/ViewTemplate')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/notificationTemplates/:customTemplateId',
    key: '/notificationTemplates/:customTemplateId',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'notificationTemplates',
        path: '/notificationTemplates',
      },
      {
        key: 'name',
      },
    ],
  },
  {
    id: 77,
    menuParentId: 45,
    name: 'Cashback Statistics',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/cashback/CashBackStatistics')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/cashbacks/:cashbackId/:calculationFrom/statistics',
    key: '/cashbacks/:cashbackId/:calculationFrom/statistics',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.cashback}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 78,
    menuParentId: 45,
    name: 'Cashback Players',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/cashback/CashBackPlayers')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/cashbacks/:cashbackId/players/:status',
    key: '/cashbacks/:cashbackId/players/:status',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.cashback}`,
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 81,
    name: 'players',
    menuParentId: 3,
    component: lazyWithRetry(() => import('features/players/pages/Players')),
    exact: true,
    path: '/players',
    key: '/players',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'players',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.players}.${resource.players}`,
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 82,
    name: 'testPlayers',
    menuParentId: 3,
    component: lazyWithRetry(() => import('features/players/pages/TestPlayers')),
    exact: true,
    path: '/testPlayers',
    key: '/testPlayers',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'testPlayers',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.players}.${resource.testPlayers}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 83,
    name: 'Test players',
    menuParentId: 82,
    component: lazyWithRetry(() => import('features/players/pages/TestPlayerUsers')),
    exact: true,
    path: '/testPlayers/:playerId/users',
    key: '/testPlayers/:playerId/users',
    brandSettings: { viewMode: true },
    timeZoneSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'testPlayers',
        path: '/testPlayers',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.players}.${resource.testPlayers}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 84,
    menuParentId: 82,
    component: lazyWithRetry(() => import('pages/Player')),
    exact: true,
    path: '/testPlayers/:playerId/*',
    key: '/testPlayers/:playerId/*',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'testPlayers',
        path: '/testPlayers',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.players}.${resource.players}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 85,
    menuParentId: 47,
    name: 'Create Referral',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/referral/CreateEditReferral')),
    exact: true,
    path: '/referrals/create',
    key: '/referrals/create',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'referral',
        path: '/referrals',
      },
      {
        name: 'create',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.bonusManagement}.${resource.referralBonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 86,
    menuParentId: 47,
    name: 'Edit Referral',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/referral/CreateEditReferral')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/referrals/:referralId/edit',
    key: '/referrals/:referralId/edit',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'referral',
        path: '/referrals',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.edit,
      rule: `${resource.bonusManagement}.${resource.referralBonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 87,
    menuParentId: 47,
    name: 'View Referral',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/referral/ViewReferral')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/referrals/:referralId',
    key: '/referrals/:referralId',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'referral',
        path: '/referrals',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.referralBonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 88,
    menuParentId: 47,
    name: 'Referral Statistics',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/referral/ReferralStatistics')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/referrals/:referralId/statistics',
    key: '/referrals/:referralId/statistics',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'referral',
        path: '/referrals',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.referralBonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 89,
    menuParentId: 47,
    name: 'Referral Referees',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/referral/ReferralReferees')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/referrals/:referralId/:referrerId/:batchId/referees',
    key: '/referrals/:referralId/:referrerId/:batchId/referees',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'referral',
        path: '/referrals',
      },
      {
        key: 'name',
        path: '/referrals/:referralId/statistics',
      },
      {
        name: 'referees',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.referralBonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 90,
    menuParentId: 47,
    name: 'Referral Participants',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/referral/ReferralParticipants')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/referrals/:referralId/participants',
    key: '/referrals/:referralId/participants',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'referral',
        path: '/referrals',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.referralBonus}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 92,
    name: 'NotFoundPage',
    component: lazyWithRetry(() => import('pages/NoMatch')),
    exact: true,
    path: '/notFound',
    key: '/notFound',
  },
  {
    id: 92,
    menuParentId: 26,
    name: 'Create User',
    component: lazyWithRetry(() => import('features/userManagement/pages/CreateUser')),
    exact: true,
    path: '/users/create',
    key: '/users/create',
    partnerSettings: { viewMode: true, includeMainPartner: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'users',
        path: '/users',
      },
      {
        name: 'create',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.userManagement}.${resource.users}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 93,
    menuParentId: 26,
    name: 'Edit User',
    component: lazyWithRetry(() => import('features/userManagement/pages/EditUser')),
    exact: true,
    path: '/users/:userId/edit',
    key: '/users/:userId/edit',
    partnerSettings: { viewMode: true, includeMainPartner: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'users',
        path: '/users',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.edit,
      rule: `${resource.userManagement}.${resource.users}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 94,
    menuParentId: 27,
    name: 'Create Permission Group',
    component: lazyWithRetry(() => import('features/userManagement/pages/CreatePermissionGroup')),
    exact: true,
    path: '/permissionGroups/create',
    key: '/permissionGroups/create',
    partnerSettings: { viewMode: true, includeMainPartner: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'permissionGroups',
        path: '/permissionGroups',
      },
      {
        name: 'create',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.userManagement}.${resource.permissionGroups}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 95,
    menuParentId: 26,
    name: 'Manage Bulk Permissions',
    component: lazyWithRetry(() => import('features/userManagement/pages/BulkManagePermissions')),
    exact: true,
    path: '/users/bulkManagePermissions',
    key: '/users/bulkManagePermissions',
    partnerSettings: { viewMode: true, includeMainPartner: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'users',
        path: '/users',
      },
      {
        name: 'managePermissions',
      },
    ],
    hide: () => !!Can({
      type: types.manage,
      rule: `${resource.userManagement}.${resource.users}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 96,
    menuParentId: 26,
    name: 'Manage Permissions',
    component: lazyWithRetry(() => import('features/userManagement/pages/ManagePermissions')),
    exact: true,
    path: '/users/:userId/managePermissions',
    key: '/users/:userId/managePermissions',
    partnerSettings: { viewMode: true, includeMainPartner: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'users',
        path: '/users',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.manage,
      rule: `${resource.userManagement}.${resource.users}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 124,
    name: 'brandAutomation',
    menuParentId: 101,
    component: lazyWithRetry(() => import('features/brandAutomation/pages/BrandAutomation')),
    exact: true,
    brandSettings: { editMode: true },
    menuItem: true,
    path: '/brandAutomation',
    key: '/brandAutomation',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'brandAutomation',
        path: '/brandAutomation',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.brandAutomation}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 50,
    menuParentId: 101,
    menuItem: true,
    name: 'segments',
    component: lazyWithRetry(() => import('pages/Segments')),
    exact: true,
    path: '/segments',
    key: '/segments',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'segments',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.tools}.${resource.playerSegments}`, `${resource.tools}.${resource.gameSegments}`],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 103,
    menuParentId: 7,
    name: 'blacklist',
    component: lazyWithRetry(() => import('pages/Blacklist')),
    exact: true,
    path: '/blackList',
    key: '/blackList',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'blacklist',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.blacklist}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 108,
    menuItem: true,
    name: 'deposits',
    menuParentId: 4,
    component: lazyWithRetry(() => import('features/payments/pages/Deposits')),
    exact: true,
    path: '/deposits',
    key: '/deposits',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'deposits',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.financials}.${resource.deposits}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 109,
    menuItem: true,
    name: 'withdrawals',
    menuParentId: 4,
    component: lazyWithRetry(() => import('features/payments/pages/Withdrawals')),
    exact: true,
    path: '/withdrawals',
    key: '/withdrawals',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'withdrawals',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.financials}.${resource.withdrawals}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 120,
    menuItem: true,
    name: 'players',
    menuParentId: 15,
    brandSettings: { editMode: true },
    component: lazyWithRetry(() => import('pages/PlayerReports')),
    exact: true,
    path: '/playerReports',
    key: '/playerReports',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'players',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [
        `${resource.playersReports}.${resource.closedAccounts}`,
        `${resource.playersReports}.${resource.playersTotals}`,
        `${resource.playersReports}.${resource.inactiveAccounts}`,
        `${resource.playersReports}.${resource.topPlayers}`,
      ],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 140,
    menuItem: true,
    name: 'businessIntelligence',
    menuParentId: 15,
    component: lazyWithRetry(() => import('pages/BiReports')),
    exact: true,
    path: '/bi',
    key: '/bi',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'businessIntelligence',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.businessIntelligence}.${resource.betAggregations}`, `${resource.businessIntelligence}.${resource.paymentAggregations}`],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 121,
    menuItem: true,
    name: 'compliance',
    menuParentId: 15,
    component: lazyWithRetry(() => import('pages/ComplianceReports')),
    exact: true,
    timeZoneSettings: { editMode: true },
    path: '/compliances',
    key: '/compliances',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'compliance',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [
        `${resource.compliance}.${resource.largeTransaction}`,
        `${resource.compliance}.${resource.negativeBalance}`,
        `${resource.compliance}.${resource.liability}`,
        `${resource.compliance}.${resource.blockedPlayers}`,
        `${resource.compliance}.${resource.playerLimitsReport}`,
      ],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 207,
    menuItem: true,
    name: 'deviceSessions',
    menuParentId: 15,
    component: lazyWithRetry(() => import('pages/DeviceSessions')),
    exact: true,
    path: '/deviceSessions',
    key: '/deviceSessions',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'deviceSessions',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.deviceSessions}.${resource.deviceSessions}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 122,
    name: 'Casino Bet TransactionDetails',
    menuParentId: 5,
    component: lazyWithRetry(() => import('features/bets/pages/CasinoBetTransactionDetails')),
    exact: true,
    path: '/bets/casinoTxnDetails/:brandId/:betId',
    key: '/bets/casinoTxnDetails/:brandId/:betId',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'casinoBetsReport',
        path: '/bets',
      },
      {
        name: 'txnDetails',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bets}.${resource.casinoBets}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 123,
    name: 'Sport Bet TransactionDetails',
    menuParentId: 5,
    component: lazyWithRetry(() => import('features/bets/pages/SportBetTransactionDetails')),
    exact: true,
    path: '/bets/sportTxnDetails/:brandId/:betId',
    key: '/bets/sportTxnDetails/:brandId/:betId',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'sportBetsReport',
        path: `/bets?tab=${title.bets.sport}`,
      },
      {
        name: 'txnDetails',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bets}.${resource.sportBets}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 125,
    menuItem: true,
    name: 'ipWhitelist',
    partnerSettings: { editMode: true, includeMainPartner: true },
    menuParentId: 18,
    component: lazyWithRetry(() => import('features/userManagement/pages/IpWhitelist')),
    exact: true,
    path: '/ipWhitelist',
    key: '/ipWhitelist',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'ipWhitelist',
        path: '/ipWhitelist',
      },
    ],
    hide: (params: RouteHideContext) => _.isEmpty(params?.partnerAccessIds)
      || !!Can({
        type: types.view,
        rule: `${resource.userManagement}.${resource.ipWhitelist}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 126,
    name: 'Brand Automation Create',
    menuParentId: 124,
    component: lazyWithRetry(() => import('features/brandAutomation/pages/CreateEditBrandAutomation')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/brandAutomation/create',
    key: '/brandAutomation/create',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'brandAutomation',
        path: '/brandAutomation',
      },
      {
        name: 'create',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.tools}.${resource.brandAutomation}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 127,
    name: 'Brand Automation Edit',
    menuParentId: 124,
    component: lazyWithRetry(() => import('features/brandAutomation/pages/CreateEditBrandAutomation')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/brandAutomation/:ruleId/edit',
    key: '/brandAutomation/:ruleId/edit',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'brandAutomation',
        path: '/brandAutomation',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.edit,
      rule: `${resource.tools}.${resource.brandAutomation}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 129,
    name: 'Brand Automation View',
    menuParentId: 124,
    component: lazyWithRetry(() => import('features/brandAutomation/pages/ViewBrandAutomation')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/brandAutomation/:ruleId',
    key: '/brandAutomation/:ruleId',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'brandAutomation',
        path: '/brandAutomation',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.brandAutomation}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 130,
    name: 'Brand Automation Statistics',
    menuParentId: 124,
    component: lazyWithRetry(() => import('features/brandAutomation/pages/BrandAutomationStatistics')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/brandAutomation/:ruleId/:type/statistics',
    key: '/brandAutomation/:ruleId/:type/statistics',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'brandAutomation',
        path: '/brandAutomation',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.brandAutomation}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 141,
    menuItem: true,
    name: 'financials',
    menuParentId: 15,
    component: lazyWithRetry(() => import('pages/FinancialReports')),
    exact: true,
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    path: '/financials',
    key: '/financials',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'financials',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.financialReports}.${resource.balanceAdjustments}`, `${resource.financialReports}.${resource.playersWallets}`, `${resource.financialReports}.${resource.firstDeposits}`],
      children: false,
      fallback: true,
    }),
  },
  {
    menuItem: true,
    id: 142,
    name: 'kycReport',
    menuParentId: 3,
    component: lazyWithRetry(() => import('features/kyc/pages/KycReport')),
    exact: true,
    path: '/kycReport',
    key: '/kycReport',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'kycReport',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.players}.${resource.kyc}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 144,
    name: 'CashBack Statistics Details',
    menuParentId: 77,
    component: lazyWithRetry(() => import('features/bonusManagement/pages/cashback/CashBackStatisticsDetails')),
    exact: true,
    path: '/cashbacks/:cashbackId/:calculationFrom/statistics/:id',
    key: '/cashbacks/:cashbackId/:calculationFrom/statistics/:id',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        name: 'statistics',
        path: '/cashbacks/:cashbackId/:calculationFrom/statistics',
      },
      {
        name: 'details',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.cashback}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 148,
    name: 'cms:cashier',
    menuItem: true,
    component: lazyWithRetry(() => import('pages/Cashier')),
    exact: true,
    menuParentId: 13,
    brandSettings: { editMode: true },
    path: '/cashiers',
    key: '/cashiers',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cms:cashier',
        path: '/cashiers',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.contentManagement}.${resource.cashierSettings}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 151,
    name: 'Select Cashback Type',
    menuParentId: 45,
    component: lazyWithRetry(() => import('pages/ChooseCashbackType')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/cashbacks/chooseCashbackType/create',
    key: '/cashbacks/chooseCashbackType/create',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        name: 'selectCashbackType',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.bonusManagement}.${resource.cashback}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 74,
    menuParentId: 45,
    name: 'CreateCashBack',
    component: lazyWithRetry(() => import('pages/CreateEditCashBack')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/cashbacks/:cashbackType/create',
    key: '/cashbacks/:cashbackType/create',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        name: 'selectCashbackType',
        path: '/cashbacks/chooseCashbackType/create',
      },
      {
        name: 'create',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.bonusManagement}.${resource.cashback}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 153,
    menuParentId: 45,
    name: 'Edit CashBack',
    component: lazyWithRetry(() => import('pages/CreateEditCashBack')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/cashbacks/:cashbackId/:cashbackType/edit',
    key: '/cashbacks/:cashbackId/:cashbackType/edit',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.edit,
      rule: `${resource.bonusManagement}.${resource.cashback}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 154,
    menuParentId: 45,
    name: 'View CashBack',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/cashback/ViewCashBack')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/cashbacks/:cashbackId/:cashbackType',
    key: '/cashbacks/:cashbackId/:cashbackType',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusManagement}.${resource.cashback}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 155,
    menuParentId: 26,
    name: 'View User',
    component: lazyWithRetry(() => import('features/userManagement/pages/ViewUser')),
    exact: true,
    path: '/users/:userId',
    key: '/users/:userId',
    partnerSettings: { viewMode: true, includeMainPartner: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'users',
        path: '/users',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.userManagement}.${resource.users}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 156,
    menuParentId: 19,
    name: 'systemSettings',
    menuItem: true,
    component: lazyWithRetry(() => import('pages/SystemSettings')),
    exact: true,
    path: '/systemSettings',
    key: '/systemSettings',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'systemSettings',
        path: '/systemSettings',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.systemSettings}.${resource.systemConversion}`,
      children: false,
      fallback: true,
    })
      && !!Can({
        type: types.view,
        rule: `${resource.systemSettings}.${resource.dataFormatting}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.view,
        rule: `${resource.systemSettings}.${resource.superiorList}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 157,
    menuParentId: 156,
    name: 'systemConversionLogs',
    component: lazyWithRetry(() => import('pages/SystemConversionLogs')),
    exact: true,
    path: '/systemSettings/systemConversions/:id/logs',
    key: '/systemSettings/systemConversions/:id/logs',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'systemConversion',
        path: '/systemConversions',
      },
      {
        key: 'name',
        path: '/systemSettings/systemConversions/:id/logs',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.systemSettings}.${resource.systemConversion}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 158,
    menuParentId: 13,
    name: 'gameManagement',
    menuItem: true,
    component: lazyWithRetry(() => import('pages/GameManagement')),
    exact: true,
    path: '/gameManagement',
    key: '/gameManagement',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gameManagement',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.contentManagement}.${resource.games}`, `${resource.contentManagement}.${resource.groups}`, `${resource.contentManagement}.${resource.gameTypes}`],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 159,
    menuParentId: 158,
    name: 'viewGame',
    component: lazyWithRetry(() => import('pages/ViewGame')),
    exact: true,
    path: '/gameManagement/games/:id',
    key: '/gameManagement/games/:id',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gameManagement',
        path: `/gameManagement?tab=${title.cms.games}`,
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.contentManagement}.${resource.games}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 161,
    menuParentId: 101,
    menuItem: true,
    name: 'reasonsSystem',
    component: lazyWithRetry(() => import('pages/ReasonsSystem')),
    exact: true,
    path: '/reasonsSystem',
    key: '/reasonsSystem',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'reasonsSystem',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.reasonsSystem}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 162,
    menuParentId: 161,
    name: 'reasonsSystem',
    component: lazyWithRetry(() => import('pages/ResourceReasons')),
    exact: true,
    path: '/resources/:id/reasons',
    key: '/resources/:id/reasons',
    timeZoneSettings: { editMode: true },
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'reasonsSystem',
        path: `/reasonsSystem?tab=${title.tools.resources}`,
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.reasonsSystem}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 164,
    menuParentId: 163,
    component: lazyWithRetry(() => import('pages/CreateEditCampaign')),
    exact: true,
    path: '/campaigns/:type/create',
    key: '/campaigns/:type/create',
    brandSettings: { viewMode: true, isNewVersion: false },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
        path: '/campaigns',
      },
      {
        name: 'create',
      },
    ],
    hide: (params: RouteHideContext) => params?.isNewVersion
      || !!Can({
        type: types.add,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 166,
    menuParentId: 163,
    component: lazyWithRetry(() => import('pages/ViewCampaign')),
    exact: true,
    path: '/campaigns/:campaignId/:type',
    key: '/campaigns/:campaignId/:type',
    brandSettings: { viewMode: true, isNewVersion: false },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
        path: '/campaigns',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 167,
    name: 'Bonus Campaign Players',
    menuParentId: 163,
    component: lazyWithRetry(() => import('pages/CampaignPlayers')),
    exact: true,
    path: '/campaigns/:campaignId/:status/:participantType/players',
    key: '/campaigns/:campaignId/:status/:participantType/players',
    brandSettings: { viewMode: true, isNewVersion: false },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
        path: '/campaigns',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 168,
    name: 'Bonus Campaign Statistics',
    menuParentId: 163,
    component: lazyWithRetry(() => import('pages/CampaignStatistics')),
    exact: true,
    path: '/campaigns/:campaignId/:type/:triggerType/statistics',
    key: '/campaigns/:campaignId/:type/:triggerType/statistics',
    brandSettings: { viewMode: true, isNewVersion: false },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
        path: '/campaigns',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 209,
    name: 'Bonus Campaign Usages',
    menuParentId: 163,
    component: lazyWithRetry(() => import('pages/CrossPlatformCampaignUsages')),
    exact: true,
    path: '/campaigns/:campaignId/usages',
    key: '/campaigns/:campaignId/usages',
    brandSettings: { viewMode: true, isNewVersion: false },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
        path: '/campaigns',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 104,
    name: 'tagCategories',
    menuParentId: 101,
    component: lazyWithRetry(() => import('pages/TagCategories')),
    exact: true,
    brandSettings: { editMode: true },
    menuItem: true,
    path: '/tagCategories',
    key: '/tagCategories',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'tagCategories',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.tools}.${resource.verticalTags}`, `${resource.tools}.${resource.horizontalTags}`],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 102,
    menuParentId: 104,
    name: 'Tags',
    component: lazyWithRetry(() => import('pages/HorizontalTags')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/tagCategories/horizontal/:categoryId/tags',
    key: '/tagCategories/horizontal/:categoryId/tags',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'tagCategories',
        path: `/tagCategories?tab=${title.tools.horizontal}`,
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.horizontalTags}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 105,
    menuParentId: 104,
    name: 'Tags',
    component: lazyWithRetry(() => import('pages/VerticalTags')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/tagCategories/vertical/:categoryId/tags',
    key: '/tagCategories/vertical/:categoryId/tags',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'tagCategories',
        path: `/tagCategories?tab=${title.tools.vertical}`,
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.verticalTags}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 170,
    menuParentId: 104,
    name: 'Tag Players',
    component: lazyWithRetry(() => import('pages/VerticalTagPlayers')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/tagCategories/vertical/:categoryId/tags/:tagId/players',
    key: '/tagCategories/vertical/:categoryId/tags/:tagId/players',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'tagCategories',
        path: `/tagCategories?tab=${title.tools.vertical}`,
      },
      {
        key: 'category',
        path: '/tagCategories/vertical/:categoryId/tags',
      },
      {
        key: 'tag',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.verticalTags}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 171,
    menuParentId: 104,
    name: 'Tag Players',
    component: lazyWithRetry(() => import('pages/HorizontalTagPlayers')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/tagCategories/horizontal/:categoryId/tags/:tagId/players',
    key: '/tagCategories/horizontal/:categoryId/tags/:tagId/players',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'tagCategories',
        path: `/tagCategories?tab=${title.tools.horizontal}`,
      },
      {
        key: 'category',
        path: '/tagCategories/horizontal/:categoryId/tags',
      },
      {
        key: 'tag',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.horizontalTags}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 172,
    name: 'View Bonus',
    menuParentId: 42,
    component: lazyWithRetry(() => import('pages/ViewBonus')),
    exact: true,
    path: '/bonuses/:bonusId/:bonusType',
    key: '/bonuses/:bonusId/:bonusType',
    brandSettings: { viewMode: true, isNewVersion: false },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bonuses',
        path: '/bonuses',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 173,
    menuItem: true,
    menuParentId: 7,
    name: 'previousAccounts',
    component: lazyWithRetry(() => import('pages/PreviousAccountsGroups')),
    exact: true,
    path: '/previousAccounts',
    key: '/previousAccounts',
    timeZoneSettings: { editMode: true },
    brandSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'previousAccounts',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.previousAccounts}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 174,
    menuParentId: 7,
    component: lazyWithRetry(() => import('pages/PreviousAccounts')),
    exact: true,
    path: '/previousAccounts/:id',
    key: '/previousAccounts/:id',
    timeZoneSettings: { editMode: true },
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'previousAccounts',
        path: '/previousAccounts',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.previousAccounts}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 175,
    name: 'agSentData',
    menuParentId: 101,
    component: lazyWithRetry(() => import('features/agentSystem/pages/AgSentData')),
    exact: true,
    menuItem: true,
    path: '/agSentData',
    key: '/agSentData',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'agSentData',
        path: '/agSentData',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.tools}.${resource.agSentData}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 178,
    name: 'lobbyManagement',
    menuParentId: 13,
    component: lazyWithRetry(() => import('pages/LobbyManagement')),
    exact: true,
    menuItem: true,
    path: '/lobbies',
    key: '/lobbies',
    timeZoneSettings: { editMode: true },
    brandSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'lobbyManagement',
        path: '/lobbies',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.contentManagement}.${resource.lobbyManagement}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 179,
    name: 'lobbyManagement',
    menuParentId: 178,
    component: lazyWithRetry(() => import('pages/ManageLobby')),
    exact: true,
    path: '/lobbies/:id/manage',
    key: '/lobbies/:id/manage',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'lobbyManagement',
        path: '/lobbies',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.edit,
      rule: `${resource.contentManagement}.${resource.lobbyManagement}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 180,
    name: 'lobbyManagement',
    menuParentId: 178,
    component: lazyWithRetry(() => import('pages/ViewLobby')),
    exact: true,
    path: '/lobbies/:id',
    key: '/lobbies/:id',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'lobbyManagement',
        path: '/lobbies',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.contentManagement}.${resource.lobbyManagement}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 181,
    name: 'lobbyProviders',
    menuParentId: 178,
    component: lazyWithRetry(() => import('pages/LobbyProviders')),
    exact: true,
    path: '/lobbies/:id/providers',
    key: '/lobbies/:id/providers',
    timeZoneSettings: { editMode: true },
    brandSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'lobbyManagement',
        path: '/lobbies',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.contentManagement}.${resource.providersAndGamesManagement}`,
      children: false,
      fallback: true,
    })
      || !!Can({
        type: types.view,
        rule: `${resource.brands}.${resource.casinoProduct}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 183,
    menuParentId: 182,
    name: 'Create Gamification',
    icon: 'PartnerIcon',
    component: lazyWithRetry(() => import('features/gamification/pages/CreateEditGamification')),
    exact: true,
    path: '/gamification/configure',
    key: '/gamification/configure',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gamification:gamification',
        path: '/gamification',
      },
      {
        name: 'configure',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.gamification}.${resource.gamification}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 184,
    menuParentId: 182,
    name: 'View Gamification',
    icon: 'PartnerIcon',
    component: lazyWithRetry(() => import('features/gamification/pages/ViewGamification')),
    exact: true,
    path: '/gamification/view',
    key: '/gamification/view',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gamification:gamification',
        path: '/gamification',
      },
      {
        name: 'view',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.gamification}.${resource.gamification}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 185,
    menuParentId: 182,
    name: 'Gamification Statistics',
    icon: 'PartnerIcon',
    component: lazyWithRetry(() => import('features/gamification/pages/GamificationStatistics')),
    exact: true,
    path: '/gamification/:gamificationId/statistics',
    key: '/gamification/:gamificationId/statistics',
    timeZoneSettings: { editMode: true },
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gamification:gamification',
        path: '/gamification',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.gamification}.${resource.gamification}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 167,
    name: 'Gamification Players',
    menuParentId: 182,
    component: lazyWithRetry(() => import('features/gamification/pages/GamificationPrivateParticipants')),
    exact: true,
    path: '/gamification/:gamificationId/players',
    key: '/gamification/:gamificationId/players',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gamification:gamification',
        path: '/gamification',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.gamification}.${resource.gamification}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 186,
    menuParentId: 158,
    name: 'gameManagement',
    component: lazyWithRetry(() => import('pages/CreateEditGameGroup')),
    exact: true,
    path: '/gameManagement/groups/create',
    key: '/gameManagement/groups/create',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gameManagement',
        path: `/gameManagement?tab=${title.cms.groups}`,
      },
      {
        name: 'create',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: [`${resource.contentManagement}.${resource.groups}`],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 187,
    menuParentId: 178,
    name: 'Lobby Logs',
    component: lazyWithRetry(() => import('pages/LobbyLogs')),
    exact: true,
    path: '/lobbies/:id/logs',
    key: '/lobbies/:id/logs',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'lobbyManagement',
        path: '/lobbies',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.contentManagement}.${resource.lobbyManagement}`],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 187,
    menuParentId: 158,
    name: 'Lobby Logs',
    component: lazyWithRetry(() => import('pages/LobbyLogs')),
    exact: true,
    path: '/lobbies/:id/logs',
    key: '/lobbies/:id/logs',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'lobbyManagement',
        path: '/lobbies',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.contentManagement}.${resource.lobbyManagement}`],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 188,
    menuParentId: 158,
    name: 'gameManagement',
    component: lazyWithRetry(() => import('pages/CreateEditGameGroup')),
    exact: true,
    path: '/gameManagement/groups/:id/edit',
    key: '/gameManagement/groups/:id/edit',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gameManagement',
        path: `/gameManagement?tab=${title.cms.groups}`,
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: [`${resource.contentManagement}.${resource.groups}`],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 189,
    menuItem: true,
    name: 'providers',
    menuParentId: 15,
    brandSettings: { editMode: true },
    showConversion: true,
    timeZoneSettings: { editMode: true },
    component: lazyWithRetry(() => import('pages/Providers')),
    exact: true,
    path: '/providers',
    key: '/providers',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'providers',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: [`${resource.providers}.${resource.providersReport}`],
      children: false,
      fallback: true,
    }),
  },
  {
    id: 190,
    component: lazyWithRetry(() => import('features/gamification/pages/SpinAdjustmentPlayers')),
    exact: true,
    menuParentId: 64,
    brandSettings: { viewMode: true },
    path: '/bulkActions/manualSpins/:id/players',
    key: '/bulkActions/manualSpins/:id/players',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bulkActions',
        path: `/bulkActions?tab=${title.bulkActions.promoSpinsAdjustments}`,
      },
      {
        name: 'gamification:manualSpinsPlayers',
      },
    ],
    hide: () => !!Can({
      type: types.manage,
      rule: `${resource.gamification}.${resource.gamification}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 191,
    name: 'exportCenter',
    menuParentId: 101,
    component: lazyWithRetry(() => import('pages/ExportHistory')),
    exact: true,
    menuItem: true,
    path: '/exportHistory',
    key: '/exportCenter',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'exportCenter',
        path: '/exportHistory',
      },
    ],
    hide: () => !!Can({
      type: types.export,
      rule: `${resource.players}.${resource.players}`,
      children: false,
      fallback: true,
    })
      && !!Can({
        type: types.export,
        rule: `${resource.bets}.${resource.casinoBets}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.bets}.${resource.sportBets}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.financials}.${resource.deposits}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.financials}.${resource.withdrawals}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.playersReports}.${resource.playersTotals}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.playersReports}.${resource.inactiveAccounts}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.bonusesReport}.${resource.bonusesReport}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.auditLogs}.${resource.playerLogs}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.auditLogs}.${resource.userLogs}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.auditLogs}.${resource.userManagementLogs}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.communications}.${resource.pushNotifications}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.businessIntelligence}.${resource.betAggregations}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.compliance}.${resource.largeTransaction}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.financialReports}.${resource.playersWallets}`,
        children: false,
        fallback: true,
      })
      && !!Can({
        type: types.export,
        rule: `${resource.deviceSessions}.${resource.deviceSessions}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 192,
    menuItem: true,
    menuParentId: 9,
    brandSettings: { editMode: true },
    name: 'pushNotifications',
    component: lazyWithRetry(() => import('features/crm/pages/PushNotifications')),
    exact: true,
    path: '/pushNotifications',
    key: '/pushNotifications',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'pushNotifications',
        path: '/pushNotifications',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.pushNotifications}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 193,
    name: 'ViewPushNotification',
    menuParentId: 192,
    component: lazyWithRetry(() => import('features/crm/pages/ViewPushNotification')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/pushNotifications/:notificationId',
    key: '/pushNotifications/:notificationId',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'pushNotifications',
        path: '/pushNotifications',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.pushNotifications}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 194,
    name: 'Compose',
    menuParentId: 192,
    component: lazyWithRetry(() => import('features/crm/pages/ComposePushNotification')),
    exact: true,
    path: '/pushNotifications/create',
    key: '/pushNotifications/create',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'pushNotifications',
        path: '/pushNotifications',
      },
      {
        name: 'compose',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.communications}.${resource.pushNotifications}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 195,
    name: 'Compose',
    menuParentId: 192,
    component: lazyWithRetry(() => import('features/crm/pages/PushNotificationRecipients')),
    exact: true,
    path: '/pushNotifications/:notificationId/recipients',
    key: '/pushNotifications/:notificationId/recipients',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'pushNotifications',
        path: '/pushNotifications',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.add,
      rule: `${resource.communications}.${resource.pushNotifications}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 196,
    menuParentId: 163,
    component: lazyWithRetry(() => import('pages/CampaignEditContents')),
    exact: true,
    path: '/campaigns/:campaignId/:type/editContent',
    key: '/campaigns/:campaignId/:type/editContent',
    brandSettings: { viewMode: true, isNewVersion: false },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
        path: '/campaigns',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => params?.isNewVersion
      || !!Can({
        type: types.manage,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 197,
    menuParentId: 182,
    name: 'Gamification Prize Statistics',
    icon: 'PartnerIcon',
    component: lazyWithRetry(() => import('features/gamification/pages/GamificationPrizeStatistics')),
    exact: true,
    path: '/gamification/:gamificationId/:prizeId/:prizeType/statistics',
    key: '/gamification/:gamificationId/:prizeId/:prizeType/statistics',
    timeZoneSettings: { editMode: true },
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gamification:gamification',
        path: '/gamification',
      },
      {
        key: 'name',
      },
      {
        key: 'prizeName',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.gamification}.${resource.gamification}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 202,
    name: 'View Bonus Content',
    menuParentId: 42,
    component: lazyWithRetry(() => import('pages/ViewBonusContent')),
    exact: true,
    path: '/bonuses/:bonusId/:bonusFamily/content',
    key: '/bonuses/:bonusId/:bonusFamily/content',
    brandSettings: { viewMode: true, isNewVersion: false },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bonuses',
        path: '/bonuses',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 200,
    menuItem: true,
    name: 'bonuses',
    menuParentId: 15,
    brandSettings: { editMode: true },
    component: lazyWithRetry(() => import('features/bonusManagement/pages/Reporting')),
    exact: true,
    path: '/bonusReports',
    key: '/bonusReports',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bonuses',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.bonusesReport}.${resource.bonusesReport}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 201,
    menuItem: true,
    menuParentId: 7,
    name: 'betStatusCorrection',
    component: lazyWithRetry(() => import('pages/BetStatusCorrections')),
    exact: true,
    path: '/betStatusCorrections',
    key: '/betStatusCorrections',
    timeZoneSettings: { editMode: true },
    brandSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'betStatusCorrection',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.casinoBetCorrection}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 202,
    menuParentId: 7,
    name: 'Bet Status Corrections Statistics',
    component: lazyWithRetry(() => import('pages/BetStatusCorrectionsStatistics')),
    exact: true,
    path: '/betStatusCorrections/:id/statistics',
    key: '/betStatusCorrections/:id/statistics',
    timeZoneSettings: { editMode: true },
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'betStatusCorrection',
        path: '/betStatusCorrections',
      },
      {
        key: 'id',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.casinoBetCorrection}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 203,
    name: 'Gamification Spins Statistics',
    menuParentId: 182,
    component: lazyWithRetry(() => import('features/gamification/pages/GamificationSpinStatistics')),
    exact: true,
    path: '/gamification/:gamificationId/spins/:type/:machineId/:spinDistributionId/statistics',
    key: '/gamification/:gamificationId/spins/:type/:machineId/:spinDistributionId/statistics',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gamification:gamification',
        path: '/gamification',
      },
      {
        key: 'name',
        path: '/gamification/:gamificationId/statistics',
      },
      {
        name: 'gamification:spinStatistics',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.gamification}.${resource.gamification}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 203,
    menuParentId: 19,
    menuItem: true,
    name: 'releaseNotes',
    component: lazyWithRetry(() => import('pages/ReleaseNotes')),
    exact: true,
    path: '/releaseNotes',
    key: '/releaseNotes',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'releaseNotes',
      },
    ],
    hide: () => !!Can({
      type: types.manage,
      rule: `${resource.systemSettings}.${resource.releaseNotes}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 204,
    menuParentId: 203,
    name: 'Create Release Note',
    component: lazyWithRetry(() => import('pages/CreateEditReleaseNote')),
    exact: true,
    path: '/releaseNotes/create',
    key: '/releaseNotes/create',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'releaseNotes',
        path: '/releaseNotes',
      },
      {
        name: 'create',
      },
    ],
    hide: () => !!Can({
      type: types.manage,
      rule: `${resource.systemSettings}.${resource.releaseNotes}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 205,
    menuParentId: 203,
    name: 'Edit Release Note',
    component: lazyWithRetry(() => import('pages/CreateEditReleaseNote')),
    exact: true,
    path: '/releaseNotes/:id/edit',
    key: '/releaseNotes/:id/edit',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'releaseNotes',
        path: '/releaseNotes',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.manage,
      rule: `${resource.systemSettings}.${resource.releaseNotes}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 206,
    name: 'Game Type Games',
    menuParentId: 158,
    component: lazyWithRetry(() => import('pages/GameTypeGames')),
    exact: true,
    path: '/gameManagement/gameTypes/:id',
    key: '/gameManagement/gameTypes/:id',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gameManagement',
        path: `/gameManagement?tab=${title.cms.gameTypes}`,
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.contentManagement}.${resource.gameTypes}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 207,
    name: 'Gamification Change Logs',
    menuParentId: 182,
    component: lazyWithRetry(() => import('features/gamification/pages/GamificationChangeLogs')),
    exact: true,
    path: '/gamification/:gamificationId/changeLogs',
    key: '/gamification/:gamificationId/changeLogs',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'gamification:gamification',
        path: '/gamification',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.gamification}.${resource.gamification}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 210,
    name: 'Lobby Game Groups',
    menuParentId: 178,
    component: lazyWithRetry(() => import('pages/LobbyGameGroups')),
    exact: true,
    path: '/lobbies/:id/gameGroups',
    key: '/lobbies/:id/gameGroups',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'lobbyManagement',
        path: '/lobbies',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.contentManagement}.${resource.groupsManagement}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 211,
    component: lazyWithRetry(() => import('features/riskManagement/pages/PaymentMethodPlayers')),
    exact: true,
    menuParentId: 64,
    brandSettings: { viewMode: true },
    path: '/bulkActions/blockedPaymentMethod/:id/players',
    key: '/bulkActions/blockedPaymentMethod/:id/players',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bulkActions',
        path: `/bulkActions?tab=${title.bulkActions.paymentMethods}`,
      },
      {
        name: 'riskManagement:blockedPaymentMethodPlayers',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.bulkBlockAccess}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 212,
    name: 'Lobby Widgets',
    menuParentId: 178,
    component: lazyWithRetry(() => import('pages/LobbyWidgets')),
    exact: true,
    path: '/lobbies/:id/widgets',
    key: '/lobbies/:id/widgets',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'lobbyManagement',
        path: '/lobbies',
      },
      {
        key: 'name',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.contentManagement}.${resource.widgets}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 213,
    name: 'Create Lobby Widget',
    component: lazyWithRetry(() => import('pages/CreateEditLobbyWidget')),
    exact: true,
    menuParentId: 178,
    path: '/lobbies/:id/widgets/:sectionType/create',
    key: '/lobbies/:id/widgets/:sectionType/create',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'lobbyManagement',
        path: '/lobbies',
      },
      {
        key: 'name',
        path: '/lobbies/:id/widgets',
      },
      {
        name: 'create',
      },
    ],
    hide: () => !!Can({
      type: types.manage,
      rule: `${resource.contentManagement}.${resource.widgets}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 214,
    name: 'Edit Lobby Widget',
    component: lazyWithRetry(() => import('pages/CreateEditLobbyWidget')),
    exact: true,
    menuParentId: 178,
    path: '/lobbies/:id/widgets/:widgetId/:sectionType/edit',
    key: '/lobbies/:id/widgets/:widgetId/:sectionType/edit',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'lobbyManagement',
        path: '/lobbies',
      },
      {
        key: 'name',
        path: '/lobbies/:id/widgets',
      },
      {
        key: 'widgetName',
      },
    ],
    hide: () => !!Can({
      type: types.manage,
      rule: `${resource.contentManagement}.${resource.widgets}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 1025,
    menuParentId: 163,
    component: lazyWithRetry(() => import('pages/CreateEditCampaignV2')),
    exact: true,
    path: '/campaigns/v2/:type/configure',
    key: '/campaigns/v2/:type/configure',
    brandSettings: { viewMode: true, isNewVersion: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
        path: '/campaigns',
      },
      {
        name: 'configure',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.add,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1026,
    menuParentId: 163,
    component: lazyWithRetry(() => import('pages/ViewCampaignV2')),
    exact: true,
    path: '/campaigns/v2/:type/view',
    key: '/campaigns/v2/:type/view',
    brandSettings: { viewMode: true, isNewVersion: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
        path: '/campaigns',
      },
      {
        name: 'view',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1029,
    menuParentId: 1028,
    component: lazyWithRetry(() => import('pages/ViewCampaignV2Unit')),
    exact: true,
    path: '/campaigns/v2/:triggerType/units/:unitId/:type/view',
    key: '/campaigns/v2/:triggerType/units/:unitId/:type/view',
    brandSettings: { viewMode: true, isNewVersion: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaignsV2Units',
        path: '/campaigns',
      },
      {
        name: 'view',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1031,
    menuParentId: 1028,
    component: lazyWithRetry(() => import('features/bonusManagement/pages/UnitStatistics')),
    exact: true,
    path: '/campaigns/v2/:campaignId/units/:unitId/statistics/:triggerType/:bonusType',
    key: '/campaigns/v2/:campaignId/units/:unitId/statistics/:triggerType/:bonusType',
    brandSettings: { viewMode: true, isNewVersion: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaignsV2Units',
        path: '/campaigns/v2/units',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1027,
    menuParentId: 163,
    component: lazyWithRetry(() => import('pages/CampaignV2Statistics')),
    name: 'campaignStatisticsV2',
    exact: true,
    path: '/campaigns/v2/:campaignId/:type/:triggerType/statistics',
    key: '/campaigns/v2/:campaignId/:type/:triggerType/statistics',
    brandSettings: { viewMode: true, isNewVersion: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
        path: '/campaigns',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1030,
    menuParentId: 1028,
    component: lazyWithRetry(() => import('features/bonusManagement/pages/UnitPlayers')),
    name: 'campaignUnitPlayersV2',
    exact: true,
    path: '/campaigns/v2/:campaignId/:unitId/unitPlayers',
    key: '/campaigns/v2/:campaignId/:unitId/unitPlayers',
    brandSettings: { viewMode: true, isNewVersion: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaignsV2Units',
        path: '/campaigns/v2/units',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1033,
    name: 'View Bonus',
    menuParentId: 42,
    component: lazyWithRetry(() => import('pages/CampaignV2ViewBonus')),
    exact: true,
    path: '/campaigns/v2/:campaignId/bonuses/:bonusId/:bonusType',
    key: '/campaigns/v2/:campaignId/bonuses/:bonusId/:bonusType',
    brandSettings: { viewMode: true, isNewVersion: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bonuses',
        path: '/bonuses',
      },
      {
        name: 'view',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    menuItem: true,
    id: 213,
    menuParentId: 9,
    name: 'playerComplaints',
    component: lazyWithRetry(() => import('pages/PlayerComplaints')),
    exact: true,
    path: '/playerComplaints',
    key: '/playerComplaints',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'playerComplaints',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.playerComplaints}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 1028,
    menuParentId: 163,
    component: lazyWithRetry(() => import('pages/CrossPlatformCampaignV2Statistics')),
    name: 'campaignV2Statistics',
    exact: true,
    path: '/campaigns/v2/:campaignId/:type/statistics',
    key: '/campaigns/v2/:campaignId/:type/statistics',
    brandSettings: { viewMode: true, isNewVersion: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'campaigns',
        path: '/campaigns',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    menuItem: true,
    id: 215,
    menuParentId: 9,
    name: 'selfAssessments',
    component: lazyWithRetry(() => import('pages/SelfAssessments')),
    exact: true,
    path: '/selfAssessments',
    key: '/selfAssessments',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'selfAssessments',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.communications}.${resource.selfAssessments}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 1035,
    name: 'UsedPromoCodesV2',
    menuParentId: 46,
    component: lazyWithRetry(() => import('pages/UsedPromoCodesV2')),
    exact: true,
    brandSettings: { viewMode: true, isNewVersion: true },
    path: '/promoCodes/v2/:id/used/:type',
    key: '/promoCodes/v2/:id/used/:type',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'promoCodes',
        path: '/promoCodes',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1037,
    name: 'Select Cashback Type',
    menuParentId: 45,
    component: lazyWithRetry(() => import('pages/ChooseCashbackV2Type')),
    exact: true,
    brandSettings: { viewMode: true, isNewVersion: true },
    path: '/cashbacks/v2/chooseCashbackType/create',
    key: '/cashbacks/v2/chooseCashbackType/create',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        name: 'selectCashbackType',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.add,
        rule: `${resource.bonusManagement}.${resource.cashback}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1038,
    menuParentId: 45,
    name: 'Create CashBack V2',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/cashbackV2/CreateEditCashBackV2')),
    exact: true,
    brandSettings: { viewMode: true, isNewVersion: true },
    path: '/cashbacks/v2/:cashbackType/create',
    key: '/cashbacks/v2/:cashbackType/create',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        name: 'selectCashbackType',
        path: '/cashbacks/v2/chooseCashbackType/create',
      },
      {
        name: 'create',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.add,
        rule: `${resource.bonusManagement}.${resource.cashback}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1039,
    menuParentId: 45,
    name: 'View CashBack V2',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/cashbackV2/ViewCashBackV2')),
    exact: true,
    brandSettings: { viewMode: true, isNewVersion: true },
    path: '/cashbacks/v2/:cashbackId/:cashbackType',
    key: '/cashbacks/v2/:cashbackId/:cashbackType',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.cashback}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1040,
    menuParentId: 45,
    name: 'Edit CashBack V2',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/cashbackV2/CreateEditCashBackV2')),
    exact: true,
    brandSettings: { viewMode: true, isNewVersion: true },
    path: '/cashbacks/v2/:cashbackId/:cashbackType/edit',
    key: '/cashbacks/v2/:cashbackId/:cashbackType/edit',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.edit,
        rule: `${resource.bonusManagement}.${resource.cashback}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1041,
    menuParentId: 45,
    name: 'Cashback Statistics',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/cashbackV2/CashBackV2Statistics')),
    exact: true,
    brandSettings: { viewMode: true, isNewVersion: true },
    path: '/cashbacks/v2/:cashbackId/:calculationFrom/statistics',
    key: '/cashbacks/v2/:cashbackId/:calculationFrom/statistics',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.cashback}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1042,
    menuParentId: 45,
    name: 'Cashback Players',
    component: lazyWithRetry(() => import('features/bonusManagement/pages/cashbackV2/CashBackV2Players')),
    exact: true,
    brandSettings: { viewMode: true, isNewVersion: true },
    path: '/cashbacks/v2/:cashbackId/players',
    key: '/cashbacks/v2/:cashbackId/players',
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'cashback',
        path: '/cashbacks',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.cashback}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1043,
    menuItem: true,
    name: 'taxation',
    menuParentId: 15,
    component: lazyWithRetry(() => import('pages/Taxation')),
    exact: true,
    path: '/taxation',
    key: '/taxation',
    brandSettings: { editMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'taxation',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.taxation}.${resource.betting}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 1044,
    name: 'Bonus Statistics',
    menuParentId: 42,
    component: lazyWithRetry(() => import('pages/CampaignV2BonusStatistics')),
    exact: true,
    path: 'campaigns/v2/bonuses/:bonusId/:bonusType/:triggerType/statistics',
    key: 'campaigns/v2/bonuses/:bonusId/:bonusType/:triggerType/statistics',
    brandSettings: { viewMode: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bonuses',
        path: '/bonuses',
      },
      {
        key: 'name',
      },
    ],
    hide: (params: RouteHideContext) => !params?.isNewVersion
      || !!Can({
        type: types.view,
        rule: `${resource.bonusManagement}.${resource.bonus}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 216,
    component: lazyWithRetry(() => import('features/riskManagement/pages/ProviderPlayers')),
    exact: true,
    menuParentId: 64,
    brandSettings: { viewMode: true },
    path: '/bulkActions/blockedProvider/:id/players',
    key: '/bulkActions/blockedProvider/:id/players',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'bulkActions',
        path: `/bulkActions?tab=${title.bulkActions.providers}`,
      },
      {
        name: 'riskManagement:blockedProviderPlayers',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.riskManagement}.${resource.bulkBlockAccess}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 217,
    name: 'Periodic Report',
    menuParentId: 140,
    component: lazyWithRetry(() => import('pages/BiPeriodicReport')),
    exact: true,
    path: '/bi/:playerId/periodicReport',
    key: '/bi/:playerId/periodicReport',
    timeZoneSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'businessIntelligence',
        path: '/bi',
      },
      {
        name: 'reporting:periodic',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.businessIntelligence}.${resource.betAggregations}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 1045,
    menuParentId: 1043,
    name: 'Taxation Statistics By Player',
    component: lazyWithRetry(() => import('pages/TaxationStatisticsByPlayer')),
    exact: true,
    brandSettings: { viewMode: true },
    path: '/taxation/byPlayer/:playerId/statistics',
    key: '/taxation/byPlayer/:playerId/statistics',
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'taxation',
        path: '/taxation',
      },
      {
        name: 'title:byPlayer',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.taxation}.${resource.betting}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 1045,
    menuParentId: 1043,
    name: 'Taxation Statistics By Category',
    component: lazyWithRetry(() => import('pages/TaxationStatisticsByCategory')),
    exact: true,
    path: '/taxation/byCategory/:taxationType/statistics',
    key: '/taxation/byCategory/:taxationType/statistics',
    brandSettings: { viewMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'taxation',
        path: '/taxation',
      },
      {
        name: 'title:byCategory',
      },
    ],
    hide: () => !!Can({
      type: types.view,
      rule: `${resource.taxation}.${resource.betting}`,
      children: false,
      fallback: true,
    }),
  },
  {
    id: 1046,
    menuParentId: 18,
    menuItem: true,
    name: 'requests',
    component: lazyWithRetry(() => import('features/userManagement/pages/Requests')),
    exact: true,
    path: '/requests',
    key: '/requests',
    partnerSettings: { editMode: true, includeMainPartner: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'requests',
        path: '/requests',
      },
    ],
    hide: (params: RouteHideContext) => _.isEmpty(params?.partnerAccessIds)
      || !!Can({
        type: types.view,
        rule: `${resource.userManagement}.${resource.requests}`,
        children: false,
        fallback: true,
      }),
  },
  {
    id: 1047,
    menuParentId: 1046,
    name: 'requests',
    component: lazyWithRetry(() => import('features/userManagement/pages/ManageRequest')),
    exact: true,
    path: '/requests/:id',
    key: '/requests/:id',
    partnerSettings: { viewMode: true, includeMainPartner: true },
    timeZoneSettings: { editMode: true },
    breadcrumb: [
      {
        name: 'home',
        path: '/',
      },
      {
        name: 'requests',
        path: '/requests',
      },
      {
        key: 'type',
      },
    ],
    hide: (params: RouteHideContext) => _.isEmpty(params?.partnerAccessIds)
      || !!Can({
        type: types.view,
        rule: `${resource.userManagement}.${resource.requests}`,
        children: false,
        fallback: true,
      }),
  },
] satisfies RouteConfig[];
